import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useSectionAccess, type SectionKey } from '../lib/roleAccess'

/**
 * Route guard for a role-gated Studio surface (MS-741 AC3 / R-ROLE-1).
 *
 * A children-wrapper, like `RequireMcpAccess`, rather than a layout route,
 * because this gates a named subset per route rather than everything below it.
 *
 * UX gate only, NOT the security boundary: it exists so a caller without
 * access is not dropped onto a page whose first fetch would 403. Real
 * enforcement is server-side — `require_global_permission`/
 * `require_agent_permission` on every endpoint the page calls. See
 * lib/roleAccess.ts.
 */
export function RequireSectionAccess({ section, children }: { section: SectionKey; children: ReactNode }) {
  const { canOpen, isRoleResolved } = useSectionAccess()
  /* Hold, don't deny, until the role is known (see SectionAccess.isRoleResolved):
     AppShell renders routes as soon as the cached session flag is set, so a
     profile written before `studioRole` existed would otherwise bounce a
     legitimate deep link to /home before the mount probe lands — and that
     redirect is never undone. */
  if (!isRoleResolved) return null
  return canOpen(section) ? <>{children}</> : <Navigate to="/home" replace />
}
